// src/pages/search/SearchResultPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../../shared/libs/firebaseConfig";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";

import { Spinner } from "react-bootstrap";
import { BsSearch, BsPlay, BsFillPlayFill, BsYoutube } from "react-icons/bs";
import { FaPodcast } from "react-icons/fa";

import { bumpVideoView } from "../../shared/utils/bumpVideoView";

import "./SearchPage.css";

// ----------------------
// helpers
// ----------------------
function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") return new Date(v);
  } catch {}
  return null;
}

function getYouTubeId(url = "") {
  try {
    const u = new URL(url);
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    if (u.hostname.includes("youtu.be")) return u.pathname.replace("/", "") || null;
    if (u.pathname.startsWith("/embed/")) return u.pathname.split("/embed/")[1]?.split("?")[0] || null;
    return null;
  } catch {
    return null;
  }
}

function ytEmbedUrl(videoId) {
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
}

// หมวดหมู่ที่คุณใช้จริง (สำหรับตรวจว่าคำค้น “ตรงหมวด” ไหม)
const CATEGORY_OPTIONS = ["เนติบัณฑิต", "ทนายความ", "อัยการ", "ผู้พิพากษา", "สอบ ก.พ.", "ก.พ."];

// ----------------------
// VideoCard (เหมือนแนว VideoUpdate แต่เป็น grid)
// ----------------------
function VideoCard({ v }) {
  const [playing, setPlaying] = useState(false);

  const href = v.url || v.fileUrl || "#";
  const isYouTubeLike = v.kind === "youtube" || v.kind === "podcast";
  const videoId = isYouTubeLike ? getYouTubeId(href) : null;

  const d = toDateSafe(v.updatedAt) || toDateSafe(v.createdAt);
  const dateText = d
    ? d.toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" })
    : "";

  return (
    <div className="card app-card h-100">
      <div className="ratio ratio-16x9 bg-dark position-relative">
        {isYouTubeLike && videoId && playing ? (
          <iframe
            src={ytEmbedUrl(videoId)}
            title={v.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ border: 0 }}
          />
        ) : (
          <>
            <img
              src={v.thumbUrl || (videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "")}
              alt={v.title}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />

            {isYouTubeLike && videoId ? (
              <button
                type="button"
                className={`video-play-btn ${v.kind === "podcast" ? "video-play-btn--podcast" : "video-play-btn--youtube"}`}
                aria-label="เล่นวิดีโอ"
                onClick={async () => {
                  setPlaying(true);
                  try { await bumpVideoView(v.id); } catch {}
                }}
              >
                {v.kind === "podcast" ? (
                  <FaPodcast size={54} className="video-play-icon" />
                ) : (
                  <BsYoutube size={54} className="video-play-icon" />
                )}
              </button>
            ) : (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="stretched-link"
                aria-label="เปิดลิงก์"
              />
            )}
          </>
        )}
      </div>

      <div className="card-body">
        <div className="fw-semibold" style={{ lineHeight: 1.25 }}>
          {v.title || "-"}
        </div>

        {/* {v.description ? (
          <div className="text-muted small mt-1" style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            lineHeight: 1.35
          }}>
            {v.description}
          </div>
        ) : null} */}

        <div className="text-secondary small mt-2">
          {dateText}
          {Array.isArray(v.categories) && v.categories.length > 0 ? <> • {v.categories[0]}</> : null}
          {typeof v.viewCounts === "number" ? <> • {v.viewCounts.toLocaleString()} views</> : null}
        </div>

        {isYouTubeLike && href && (
          <div className="mt-2">
            <a href={href} target="_blank" rel="noreferrer" className="small text-decoration-none link-primary">
              เปิดใน {v.kind === "podcast" ? "Podcast" : "YouTube"} ›
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SearchResultPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") || "";

  const [queryText, setQueryText] = useState(qParam);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => setQueryText(qParam), [qParam]);

  useEffect(() => {
    const termRaw = qParam.trim();
    const term = termRaw.toLowerCase();

    if (!term) {
      setItems([]);
      return;
    }

    const run = async () => {
      setLoading(true);
      try {
        const ref = collection(db, "videos");

        const isExactCategory = CATEGORY_OPTIONS.some((c) => c.toLowerCase() === term);

        let snap;

        if (isExactCategory) {
          // ✅ เร็วที่สุด: ตรงหมวด -> array-contains
          const q1 = query(
            ref,
            where("isActive", "==", true),
            where("categories", "array-contains", termRaw),  // ต้องตรงคำ (case-sensitive) ตามที่เก็บ
            orderBy("updatedAt", "desc"),
            limit(100)
          );
          snap = await getDocs(q1);
        } else {
          // ✅ fallback: ดึงชุดหนึ่งแล้ว filter ฝั่ง client ให้ค้นหาแบบ contains ได้
          const q2 = query(
            ref,
            where("isActive", "==", true),
            orderBy("updatedAt", "desc"),
            limit(200)
          );
          snap = await getDocs(q2);
        }

        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // ✅ filter ฝั่ง client (รองรับค้นหาบางส่วน)
        const filtered = rows.filter((x) => {
          const title = (x.title || "").toLowerCase();
          const desc = (x.description || "").toLowerCase();
          const kind = (x.kind || "").toLowerCase();
          const cats = Array.isArray(x.categories) ? x.categories.map((c) => String(c).toLowerCase()) : [];

          return (
            title.includes(term) ||
            desc.includes(term) ||
            kind.includes(term) ||
            cats.some((c) => c.includes(term))
          );
        });

        // ✅ รวม youtube + podcast (จริง ๆ ไม่ต้องกรอง เพราะ filter ข้างบนไม่ตัด)
        const finalList = filtered.filter((x) => x.kind === "youtube" || x.kind === "podcast");

        setItems(finalList);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [qParam]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const term = queryText.trim();
    if (!term) return;
    setSearchParams({ q: term });
  };

  const total = items.length;

  return (
    <div className="container py-4 search-page">
      <form className="mb-3" onSubmit={handleSubmit}>
        <div className="header-search-box" style={{ maxWidth: 480 }}>
          <span className="header-search-icon"><BsSearch /></span>
          <input
            type="search"
            className="form-control header-search-input"
            placeholder="ค้นหาใน ChicChic"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            enterKeyHint="search"
          />
        </div>
      </form>

      {qParam ? (
        <div className="mb-3">
          <h4 className="m-0">
            ผลลัพธ์สำหรับ{" "}
            <span style={{ fontWeight: 600 }}>"{qParam}"</span>
          </h4>
          <div className="small text-muted">พบ {total} วิดีโอ</div>
        </div>
      ) : (
        <div className="text-muted mb-3">
          พิมพ์คำค้นหาในช่องด้านบนเพื่อค้นหาวิดีโอ
        </div>
      )}

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center my-4"
          style={{ minHeight: "120px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">กำลังค้นหา…</span>
          </Spinner>
        </div>
      ) : !qParam ? null : total === 0 ? (
        <div className="text-muted">ไม่พบวิดีโอที่ตรงกับคำค้นหา</div>
      ) : (
        <div className="row g-3 mt-2">
          {items.map((v) => (
            <div key={v.id} className="col-12 col-sm-6 col-lg-4">
              <VideoCard v={v} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

